/**
 * View Snap Manager
 * 
 * Manages camera view presets and animated transitions between views.
 * Supports standard views (top, front, etc.) and custom presets.
 */

import * as THREE from 'three';
import type { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import type { ViewPreset, CameraDefaults, IManager } from '../types';

export interface ViewSnapManagerConfig {
  viewPresets?: ViewPreset[];
  cameraDefaults: CameraDefaults;
}

// Standard view presets including top, bottom, sides, and corners
const STANDARD_VIEWS: ViewPreset[] = [
  // Top & Bottom
  { id: 'top', name: 'Top', position: [0, 5, 0.001], target: [0, 0, 0], icon: 'top', hotkey: '1', group: 'vertical' },
  { id: 'bottom', name: 'Bottom', position: [0, -5, 0.001], target: [0, 0, 0], icon: 'bottom', hotkey: '2', group: 'vertical' },
  // Sides
  { id: 'front', name: 'Front', position: [0, 0, 5], target: [0, 0, 0], icon: 'front', hotkey: '3', group: 'sides' },
  { id: 'back', name: 'Back', position: [0, 0, -5], target: [0, 0, 0], icon: 'back', hotkey: '4', group: 'sides' },
  { id: 'left', name: 'Left', position: [-5, 0, 0], target: [0, 0, 0], icon: 'left', hotkey: '5', group: 'sides' },
  { id: 'right', name: 'Right', position: [5, 0, 0], target: [0, 0, 0], icon: 'right', hotkey: '6', group: 'sides' },
  // Corners (isometric-style views)
  { id: 'front-right', name: 'Front Right', position: [3.5, 2.5, 3.5], target: [0, 0, 0], icon: 'corner-fr', hotkey: '7', group: 'corners' },
  { id: 'front-left', name: 'Front Left', position: [-3.5, 2.5, 3.5], target: [0, 0, 0], icon: 'corner-fl', hotkey: '8', group: 'corners' },
  { id: 'back-right', name: 'Back Right', position: [3.5, 2.5, -3.5], target: [0, 0, 0], icon: 'corner-br', hotkey: '9', group: 'corners' },
  { id: 'back-left', name: 'Back Left', position: [-3.5, 2.5, -3.5], target: [0, 0, 0], icon: 'corner-bl', hotkey: '0', group: 'corners' },
];

export class ViewSnapManager implements IManager {
  private camera: THREE.PerspectiveCamera | null = null;
  private controls: OrbitControls | null = null;
  private viewPresets: ViewPreset[] = [];
  private defaultView: CameraDefaults | null = null;
  
  // Animation
  private isAnimating = false;
  private animationDuration = 0.5; // seconds
  private animationProgress = 0;
  private startPosition = new THREE.Vector3();
  private startTarget = new THREE.Vector3();
  private endPosition = new THREE.Vector3();
  private endTarget = new THREE.Vector3();
  
  // Callbacks
  private onAnimationComplete?: () => void;
  private requestRender?: () => void;
  private onPresetChange?: (presetId: string | null) => void;
  
  private activePresetId: string | null = null;
  
  /**
   * Initialize with camera and controls
   */
  public async init(_scene: THREE.Scene, config: ViewSnapManagerConfig): Promise<void> {
    this.defaultView = config.cameraDefaults;
    
    // Combine standard views with custom presets
    this.viewPresets = [
      ...STANDARD_VIEWS,
      ...(config.viewPresets ?? []),
    ];
    
    // Scale standard views based on camera distance
    const avgDistance = (config.cameraDefaults.minDistance + config.cameraDefaults.maxDistance) / 2;
    for (const preset of STANDARD_VIEWS) {
      preset.position = preset.position.map((v) => v * (avgDistance / 5)) as [number, number, number];
      preset.target = [...config.cameraDefaults.target];
    }
  }
  
  /**
   * Set camera and controls for view manipulation
   */
  public setupCamera(
    camera: THREE.PerspectiveCamera, 
    controls: OrbitControls,
    requestRender?: () => void,
    onPresetChange?: (presetId: string | null) => void
  ): void {
    this.camera = camera;
    this.controls = controls;
    this.requestRender = requestRender;
    this.onPresetChange = onPresetChange;

    // Listen for manual user interaction to clear the active preset
    this.controls.addEventListener('start', () => {
      // Only clear if we are not currently animating to a new view
      // Note: OrbitControls fires 'start' even when we programmatically update it? 
      // Actually, OrbitControls 'start' is usually for user interaction (mousedown).
      // If we update target, it fires change, but start/end are usually interaction.
      // However, to be safe, we check if we are not animating.
      if (!this.isAnimating && this.activePresetId !== null) {
        this.activePresetId = null;
        this.onPresetChange?.(null);
      }
    }); // Fixed closing
  }
  
  /**
   * Get all available view presets
   */
  public getPresets(): ViewPreset[] {
    return this.viewPresets;
  }
  
  /**
   * Get standard view presets only
   */
  public getStandardPresets(): ViewPreset[] {
    return STANDARD_VIEWS;
  }
  
  /**
   * Snap to a view preset with animation
   */
  public snapToView(presetId: string, animate = true): void {
    const preset = this.viewPresets.find((p) => p.id === presetId);
    if (!preset || !this.camera || !this.controls) return;
    
    // Set active preset
    this.activePresetId = presetId;
    this.onPresetChange?.(presetId);
    
    if (animate) {
      this.animateToView(preset.position, preset.target);
    } else {
      this.camera.position.set(...preset.position);
      this.controls.target.set(...preset.target);
      this.controls.update();
      this.requestRender?.();
    }
  }

  /**
   * Get the currently active preset ID (if any)
   */
  public getActivePresetId(): string | null {
    return this.activePresetId;
  }

  
  /**
   * Snap to default view
   */
  public snapToDefault(animate = true): void {
    if (!this.defaultView || !this.camera || !this.controls) return;
    
    if (animate) {
      this.animateToView(this.defaultView.position, this.defaultView.target);
    } else {
      this.camera.position.set(...this.defaultView.position);
      this.controls.target.set(...this.defaultView.target);
      this.controls.update();
      this.requestRender?.();
    }
  }
  
  /**
   * Animate camera to new position/target
   */
  private animateToView(
    position: [number, number, number], 
    target: [number, number, number]
  ): void {
    if (!this.camera || !this.controls) return;
    
    // Store current state
    this.startPosition.copy(this.camera.position);
    this.startTarget.copy(this.controls.target);
    
    // Set target state
    this.endPosition.set(...position);
    this.endTarget.set(...target);
    
    // Start animation
    this.isAnimating = true;
    this.animationProgress = 0;
  }
  
  /**
   * Focus on a specific object/bounds
   */
  public focusOnObject(
    object: THREE.Object3D, 
    padding = 1.5,
    animate = true
  ): void {
    if (!this.camera || !this.controls) return;
    
    // Calculate bounding box
    const box = new THREE.Box3().setFromObject(object);
    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());
    
    // Calculate required distance
    const maxDim = Math.max(size.x, size.y, size.z);
    const fov = this.camera.fov * (Math.PI / 180);
    const distance = (maxDim / 2 / Math.tan(fov / 2)) * padding;
    
    // Get current direction
    const direction = new THREE.Vector3()
      .subVectors(this.camera.position, this.controls.target)
      .normalize();
    
    // Calculate new position
    const newPosition: [number, number, number] = [
      center.x + direction.x * distance,
      center.y + direction.y * distance,
      center.z + direction.z * distance,
    ];
    const newTarget: [number, number, number] = center.toArray() as [number, number, number];
    
    if (animate) {
      this.animateToView(newPosition, newTarget);
    } else {
      this.camera.position.set(...newPosition);
      this.controls.target.set(...newTarget);
      this.controls.update();
      this.requestRender?.();
    }
  }
  
  /**
   * Handle keyboard shortcuts
   */
  public handleKeyDown(event: KeyboardEvent): boolean {
    // Find preset with matching hotkey
    const preset = this.viewPresets.find((p) => p.hotkey === event.key);
    if (preset) {
      this.snapToView(preset.id);
      return true;
    }
    
    // R for reset
    if (event.key.toLowerCase() === 'r') {
      this.snapToDefault();
      return true;
    }
    
    // F for focus (fit to view)
    if (event.key.toLowerCase() === 'f') {
      // Focus on entire scene
      if (this.camera && this.controls) {
        // This would need the model reference - emit event instead
        return true;
      }
    }
    
    return false;
  }
  
  /**
   * Set animation complete callback
   */
  public setOnAnimationComplete(callback: () => void): void {
    this.onAnimationComplete = callback;
  }
  
  /**
   * Easing function (ease-in-out cubic)
   */
  private easeInOutCubic(t: number): number {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }
  
  /**
   * Update animation (called every frame)
   */
  public update(deltaTime: number): void {
    if (!this.isAnimating || !this.camera || !this.controls) return;
    
    this.animationProgress += deltaTime / this.animationDuration;
    
    if (this.animationProgress >= 1) {
      // Animation complete
      this.camera.position.copy(this.endPosition);
      this.controls.target.copy(this.endTarget);
      this.isAnimating = false;
      this.onAnimationComplete?.();
    } else {
      // Interpolate
      const t = this.easeInOutCubic(this.animationProgress);
      
      this.camera.position.lerpVectors(this.startPosition, this.endPosition, t);
      this.controls.target.lerpVectors(this.startTarget, this.endTarget, t);
    }
    
    this.controls.update();
    this.requestRender?.();
  }
  
  /**
   * Check if currently animating
   */
  public isTransitioning(): boolean {
    return this.isAnimating;
  }
  
  /**
   * Cancel current animation
   */
  public cancelAnimation(): void {
    this.isAnimating = false;
  }
  
  /**
   * Add a custom view preset
   */
  public addPreset(preset: ViewPreset): void {
    // Check if ID already exists
    const existing = this.viewPresets.findIndex((p) => p.id === preset.id);
    if (existing >= 0) {
      this.viewPresets[existing] = preset;
    } else {
      this.viewPresets.push(preset);
    }
  }
  
  /**
   * Remove a view preset
   */
  public removePreset(presetId: string): void {
    const index = this.viewPresets.findIndex((p) => p.id === presetId);
    if (index >= 0) {
      this.viewPresets.splice(index, 1);
    }
  }
  
  /**
   * Save current view as preset
   */
  public saveCurrentAsPreset(id: string, name: string): ViewPreset {
    if (!this.camera || !this.controls) {
      throw new Error('Camera not initialized');
    }
    
    const preset: ViewPreset = {
      id,
      name,
      position: this.camera.position.toArray() as [number, number, number],
      target: this.controls.target.toArray() as [number, number, number],
    };
    
    this.addPreset(preset);
    return preset;
  }
  
  /**
   * Snap to the nearest flat (orthogonal) view based on current camera direction.
   * Used for Ctrl+click snap-to-view functionality.
   * Only considers flat views: front, back, left, right, top, bottom (not corners).
   */
  public snapToNearestFlatView(animate = true): void {
    if (!this.camera || !this.controls) return;
    
    // Get current camera direction (from target to camera)
    const cameraDir = new THREE.Vector3()
      .subVectors(this.camera.position, this.controls.target)
      .normalize();
    
    // Define the 6 orthogonal directions and their corresponding view IDs
    const flatViews: Array<{ id: string; direction: THREE.Vector3 }> = [
      { id: 'front', direction: new THREE.Vector3(0, 0, 1) },    // +Z
      { id: 'back', direction: new THREE.Vector3(0, 0, -1) },    // -Z
      { id: 'left', direction: new THREE.Vector3(-1, 0, 0) },    // -X
      { id: 'right', direction: new THREE.Vector3(1, 0, 0) },    // +X
      { id: 'top', direction: new THREE.Vector3(0, 1, 0) },      // +Y
      { id: 'bottom', direction: new THREE.Vector3(0, -1, 0) },  // -Y
    ];
    
    // Find the view whose direction is most aligned with current camera direction
    // (highest dot product = smallest angle between directions)
    let bestView = flatViews[0];
    let bestDot = -Infinity;
    
    for (const view of flatViews) {
      const dot = cameraDir.dot(view.direction);
      if (dot > bestDot) {
        bestDot = dot;
        bestView = view;
      }
    }
    
    // Snap to the nearest flat view
    this.snapToView(bestView.id, animate);
  }
  
  /**
   * Snap to a flat view based on drag direction.
   * Dragging rotates the view in that direction and snaps to the nearest orthogonal view.
   * @param deltaX - Horizontal drag distance (positive = right)
   * @param deltaY - Vertical drag distance (positive = down)
   * @param animate - Whether to animate the transition
   */
  public snapInDragDirection(deltaX: number, deltaY: number, animate = true): void {
    if (!this.camera || !this.controls) return;
    
    // Get current camera direction (from target to camera)
    const cameraDir = new THREE.Vector3()
      .subVectors(this.camera.position, this.controls.target)
      .normalize();
    
    // Get camera's right and up vectors in world space
    const cameraRight = new THREE.Vector3();
    const cameraUp = new THREE.Vector3();
    this.camera.getWorldDirection(new THREE.Vector3()); // Updates matrix
    cameraRight.setFromMatrixColumn(this.camera.matrixWorld, 0).normalize();
    cameraUp.setFromMatrixColumn(this.camera.matrixWorld, 1).normalize();
    
    // Convert 2D drag to 3D rotation direction
    // Dragging right → camera rotates left (view moves right) → camera direction rotates around up axis
    // Dragging up → camera rotates down (view moves up) → camera direction rotates around right axis
    // We want to find the "intended" new camera direction based on drag
    
    // Normalize drag to get a reasonable rotation influence
    const dragLength = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    const normalizedX = deltaX / dragLength;
    const normalizedY = deltaY / dragLength;
    
    // Calculate the intended camera direction by rotating current direction
    // Horizontal drag rotates around world Y (vertical axis)
    // Vertical drag rotates around camera's right axis
    const rotationStrength = 1.5; // How much the drag influences the direction
    
    const intendedDir = cameraDir.clone();
    
    // Apply horizontal rotation (around Y axis) - dragging right rotates camera left
    if (Math.abs(normalizedX) > 0.1) {
      const yRotation = new THREE.Quaternion();
      yRotation.setFromAxisAngle(new THREE.Vector3(0, 1, 0), -normalizedX * rotationStrength);
      intendedDir.applyQuaternion(yRotation);
    }
    
    // Apply vertical rotation (around camera right axis) - dragging up rotates camera down
    if (Math.abs(normalizedY) > 0.1) {
      const xRotation = new THREE.Quaternion();
      xRotation.setFromAxisAngle(cameraRight, normalizedY * rotationStrength);
      intendedDir.applyQuaternion(xRotation);
    }
    
    intendedDir.normalize();
    
    // Define the 6 orthogonal directions and their corresponding view IDs
    const flatViews: Array<{ id: string; direction: THREE.Vector3 }> = [
      { id: 'front', direction: new THREE.Vector3(0, 0, 1) },    // +Z
      { id: 'back', direction: new THREE.Vector3(0, 0, -1) },    // -Z
      { id: 'left', direction: new THREE.Vector3(-1, 0, 0) },    // -X
      { id: 'right', direction: new THREE.Vector3(1, 0, 0) },    // +X
      { id: 'top', direction: new THREE.Vector3(0, 1, 0) },      // +Y
      { id: 'bottom', direction: new THREE.Vector3(0, -1, 0) },  // -Y
    ];
    
    // Find the view whose direction is most aligned with the intended direction
    let bestView = flatViews[0];
    let bestDot = -Infinity;
    
    for (const view of flatViews) {
      const dot = intendedDir.dot(view.direction);
      if (dot > bestDot) {
        bestDot = dot;
        bestView = view;
      }
    }
    
    // Snap to the best matching flat view
    this.snapToView(bestView.id, animate);
  }
  
  /**
   * Dispose
   */
  public dispose(): void {
    this.camera = null;
    this.controls = null;
    this.viewPresets = [];
  }
}
